import { WidgetPlaceType, IBorderRadiusData, SelfPosition, INavigationLinkData, IInteractiveElementColorData, ILinkData, IPxHeightData, IWidgetVisibility, VisibilityData } from "../index";
import { IAnimatedWidget } from "../widget-style-data";
import { Guid } from "../../utils/guid-util";

export interface IMemberMenuWidgetData extends IWidgetVisibility {
    readonly type: "MemberMenuWidget";
    placeType: WidgetPlaceType;
    id?: string | null;
    menuLinkItems?: INavigationLinkData[] | null;
    layout: MemberMenuLayout;
    style: IMemberMenuStyleData;
    iconArrangement: MemberMenuIconArrangement;
    ownerData?: IMemberMenuOwnerData;
}

export enum MemberMenuLayout {
    Text = "Text",
    IconAndText = "IconAndText",
    TextWithBackground = "TextWithBackground"
}

export interface IMemberMenuStyleData extends IAnimatedWidget {
    backgroundColor?: string | null;
    borderRadius: IBorderRadiusData;
    alignment: SelfPosition;
    icon: IMemberMenuIconStylesData;
    dropdown?: IMemberMenuDropdownStylesData;
    linkColorStyleData: IInteractiveElementColorData;
    buttonColor: IInteractiveElementColorData;
    heightData?: IPxHeightData | null;
}

export interface IMemberMenuIconStylesData {
    backgroundColor?: string | null;
    color?: string | null;
}

export enum MemberMenuIconArrangement {
    AlignRight = "AlignRight",
    AlignLeft = "AlignLeft"
}

export interface IMemberMenuDropdownStylesData {
    backgroundColor?: string | null;
    borderRadius: IBorderRadiusData;
    itemHoverColor?: string | null;
    linkColorStyleData: IInteractiveElementColorData;
}

export interface IMemberMenuOwnerData {
    firstName?: string | null;
    avatarSrc?: string | null;
}

export interface IMemberMenuShortData {
    menuLinkItems?: INavigationLinkData[] | null;
}

export abstract class MemberMenuWidgetData {

    public static get defaultData(): IMemberMenuWidgetData {
        return {
            type: "MemberMenuWidget",
            placeType: "Section",
            menuLinkItems: [],
            layout: MemberMenuLayout.Text,
            style: this.defaultStyle,
            iconArrangement: MemberMenuIconArrangement.AlignLeft,
            visibility: VisibilityData.defaultData()
        };
    }

    public static get defaultStyle(): IMemberMenuStyleData {
        return {
            backgroundColor: "rgba(255, 255, 255, 0)",
            borderRadius: {
                topLeftPx: 0,
                topRightPx: 0,
                bottomLeftPx: 0,
                bottomRightPx: 0
            },
            alignment: "start",
            icon: this.defaultIconStyle,
            dropdown: this.defaultDropdownStyle,
            linkColorStyleData: {
                colorDefault: null,
                colorHovered: null
            },
            buttonColor: {
                colorDefault: null,
                colorHovered: null
            },
            animation: {
                animationSource: "site-design",
                animationData: {
                    name: "none",
                    duration: 1
                },
            },
            heightData: {
                height: 52
            }
        };
    }

    public static get defaultIconStyle(): IMemberMenuIconStylesData {
        return {
            backgroundColor: "rgba(255, 255, 255, 0)",
            color: "#7b828e"
        };
    }

    public static get defaultDropdownStyle(): IMemberMenuDropdownStylesData {
        return {
            backgroundColor: "#ffffff",
            borderRadius: {
                topLeftPx: 0,
                topRightPx: 0,
                bottomLeftPx: 0,
                bottomRightPx: 0
            },
            linkColorStyleData: {
                colorDefault: "#202020",
                colorHovered: "#888888"
            },
            itemHoverColor: "#ffffff"
        };
    }

    public static localScriptRerenderNeeded(leftHandSide?: IMemberMenuWidgetData, rightHandSide?: IMemberMenuWidgetData): boolean {
        return leftHandSide?.placeType === rightHandSide?.placeType &&
            leftHandSide?.id === rightHandSide?.id &&
            leftHandSide?.layout === rightHandSide?.layout &&
            leftHandSide?.style?.backgroundColor === rightHandSide?.style?.backgroundColor &&
            leftHandSide?.style?.borderRadius?.topLeftPx === rightHandSide?.style?.borderRadius?.topLeftPx &&
            leftHandSide?.style?.borderRadius?.topRightPx === rightHandSide?.style?.borderRadius?.topRightPx &&
            leftHandSide?.style?.borderRadius?.bottomLeftPx === rightHandSide?.style?.borderRadius?.bottomLeftPx &&
            leftHandSide?.style?.borderRadius?.bottomRightPx === rightHandSide?.style?.borderRadius?.bottomRightPx &&
            leftHandSide?.style?.alignment === rightHandSide?.style?.alignment &&
            leftHandSide?.style?.icon?.color === rightHandSide?.style?.icon?.color &&
            leftHandSide?.style?.dropdown?.backgroundColor === rightHandSide?.style?.dropdown?.backgroundColor &&
            leftHandSide?.style?.dropdown?.borderRadius?.topLeftPx === rightHandSide?.style?.dropdown?.borderRadius?.topLeftPx &&
            leftHandSide?.style?.dropdown?.borderRadius?.topRightPx === rightHandSide?.style?.dropdown?.borderRadius?.topRightPx &&
            leftHandSide?.style?.dropdown?.borderRadius?.bottomLeftPx === rightHandSide?.style?.dropdown?.borderRadius?.bottomLeftPx &&
            leftHandSide?.style?.dropdown?.borderRadius?.bottomRightPx === rightHandSide?.style?.dropdown?.borderRadius?.bottomRightPx &&
            leftHandSide?.style?.dropdown?.itemHoverColor === rightHandSide?.style?.dropdown?.itemHoverColor &&
            leftHandSide?.iconArrangement === rightHandSide?.iconArrangement;
    }

    private static createNavigationLink(id: string, text: string, pageValue: string): INavigationLinkData {
        return {
            id: Guid.newGuid(),
            text,
            value: {
                type: "page",
                value: pageValue,
                target: "_self"
            } as ILinkData
        };
    }

    public static addStoreNavigationLinks(menuLinkItems: INavigationLinkData[]): void {
        const myOrdersItem = this.createNavigationLink(Guid.newGuid(), "My Orders", "my-orders");
        const mySubscriptionsItem = this.createNavigationLink(Guid.newGuid(), "My Subscriptions", "my-subscriptions");

        const alreadyExists = (item: INavigationLinkData): boolean =>
            menuLinkItems.some(existing =>
                existing.value.type === item.value.type && existing.value.value === item.value.value
            );

        if (!alreadyExists(myOrdersItem)) {
            menuLinkItems.push(myOrdersItem);
        }
        if (!alreadyExists(mySubscriptionsItem)) {
            menuLinkItems.push(mySubscriptionsItem);
        }
    }

}
